import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ImageIcon, AlertCircle } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VerifyImagesList = ({ cartCount }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/admin/products`).then((r) => {
      setProducts(r.data || []);
      setLoading(false);
    });
  }, []);

  const filtered = products.filter((p) => {
    if (filter === "all") return true;
    return p.category === filter;
  });

  const categories = Array.from(new Set(products.map((p) => p.category)));

  return (
    <>
      <Header cartCount={cartCount} />
      <div className="max-w-[1400px] mx-auto px-6 pt-28 md:pt-36 pb-20" data-testid="verify-images-list">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">
              Admin · Image curation
            </div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight leading-none">
              <span className="font-black">Verify</span>{" "}
              <span className="font-thin">product images.</span>
            </h1>
            <p className="mt-3 text-sm text-neutral-600 font-light max-w-2xl">
              Each product is scraped with 10 candidate images. Pick up to 6 to display on the product page, or trigger a re-scrape.
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium border-b border-black pb-1"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>

        <div className="flex gap-2 mb-8 flex-wrap" data-testid="category-filters">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 text-xs tracking-wider uppercase border ${
              filter === "all" ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"
            }`}
          >
            All ({products.length})
          </button>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-xs tracking-wider uppercase border ${
                filter === c ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"
              }`}
              data-testid={`filter-${c}`}
            >
              {c} ({products.filter((p) => p.category === c).length})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-neutral-200">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-square bg-neutral-50 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-neutral-200 border border-neutral-200">
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={`/verifyimages/${p.id}`}
                className="bg-white group hover:bg-neutral-50 transition-colors"
                data-testid={`verify-card-${p.id}`}
              >
                <div className="relative aspect-square overflow-hidden bg-neutral-100">
                  <img
                    src={p.primary_image}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <span
                      className={`text-[10px] tracking-wider uppercase px-2 py-0.5 font-bold ${
                        p.candidate_count >= 6
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}
                    >
                      {p.candidate_count}/10
                    </span>
                  </div>
                  {p.candidate_count < 4 && (
                    <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1 bg-red-50 border border-red-200 px-2 py-1">
                      <AlertCircle className="w-3 h-3 text-red-600 shrink-0" />
                      <span className="text-[10px] text-red-700 font-medium">Low coverage</span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">{p.category}</div>
                  <div className="font-heading text-sm font-bold leading-tight mt-1 line-clamp-2 min-h-[2.4rem]">
                    {p.name}
                  </div>
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-neutral-600">
                    <ImageIcon className="w-3 h-3" />
                    <span>{p.display_count} displayed</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};
